import React from 'react';
import { Link } from 'react-router-dom';
import SEOHelmet from '../components/SEOHelmet';
import SkipToContent from '../components/SkipToContent';
import OtherStates from '../components/OtherStates';

interface ProfessionInfo {
  id: string;
  name: string;
  icon: string;
  credits: number;
  cycleYears: number;
  terminology: string;
  highlights: string[];
  stateBoard: string;
  boardUrl: string;
}

const INLicenseRenewal: React.FC = () => {
  const professions: ProfessionInfo[] = [
    {
      id: 'ARCH',
      name: 'Architect',
      icon: '🏛️',
      credits: 0,
      cycleYears: 2,
      terminology: 'LU',
      highlights: [
        'Indiana has no mandatory state CE requirement',
        'NCARB recommends 24 LU biennial (model law)',
        'Health, Safety & Welfare topics recommended',
        'Voluntary participation encouraged',
        'License renewed every 2 years with IPLA'
      ],
      stateBoard: 'Indiana Professional Licensing Agency - Architecture',
      boardUrl: 'https://www.in.gov/pla/professions/architects/'
    },
    {
      id: 'PE',
      name: 'Professional Engineer',
      icon: '⚙️',
      credits: 0,
      cycleYears: 2,
      terminology: 'PDH',
      highlights: [
        'Indiana has no mandatory state CE requirement for PEs',
        'NSPE and NCEES recommend 30 PDH biennial',
        'Professional development strongly encouraged',
        'Ethics training recommended annually',
        'License renewed biennially with IPLA'
      ],
      stateBoard: 'Indiana Professional Licensing Agency - Engineering',
      boardUrl: 'https://www.in.gov/pla/professions/engineers/'
    },
    {
      id: 'CPA',
      name: 'Certified Public Accountant',
      icon: '💼',
      credits: 120,
      cycleYears: 3,
      terminology: 'CPE',
      highlights: [
        '120 CPE hours per 3-year rolling period',
        '6 hours Ethics (mandatory)',
        'Min 20 hours per year',
        '50% technical subject minimum',
        'NASBA-approved providers accepted'
      ],
      stateBoard: 'Indiana Board of Accountancy',
      boardUrl: 'https://www.in.gov/pla/professions/accountants/'
    },
    {
      id: 'ATT',
      name: 'Attorney',
      icon: '⚖️',
      credits: 36,
      cycleYears: 3,
      terminology: 'CLE',
      highlights: [
        '36 CLE hours per 3-year reporting period',
        '3 hours Professional Conduct (mandatory)',
        'Max 6 self-study hours per reporting period',
        'Carry-forward of up to 12 hours allowed',
        'Annual reporting required'
      ],
      stateBoard: 'Indiana Supreme Court - Commission for CLE',
      boardUrl: 'https://www.in.gov/courts/iocs/cle/'
    },
    {
      id: 'RN',
      name: 'Registered Nurse',
      icon: '⚕️',
      credits: 0,
      cycleYears: 2,
      terminology: 'contact hours',
      highlights: [
        'Indiana has no mandatory CE for RN renewal',
        'Competency maintained via practice',
        'ANCC and specialty certifications recommended',
        'License renewed biennially with IPLA',
        'Voluntary CE strongly encouraged'
      ],
      stateBoard: 'Indiana State Board of Nursing',
      boardUrl: 'https://www.in.gov/pla/professions/nurses/'
    },
    {
      id: 'PHARM',
      name: 'Pharmacist',
      icon: '💊',
      credits: 30,
      cycleYears: 2,
      terminology: 'CE',
      highlights: [
        '30 CE hours biennial',
        '3 hours patient safety (mandatory)',
        '2 hours Indiana pharmacy law (mandatory)',
        'ACPE-accredited providers required',
        'License renewed every 2 years'
      ],
      stateBoard: 'Indiana Board of Pharmacy',
      boardUrl: 'https://www.in.gov/pla/professions/pharmacists/'
    }
  ];

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Indiana Professional License Renewal Requirements",
      "url": "https://cectrack.com/in-license-renewal",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://cectrack.com" },
          { "@type": "ListItem", "position": 2, "name": "Indiana License Renewal", "item": "https://cectrack.com/in-license-renewal" }
        ]
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Do Indiana architects and engineers have mandatory continuing education requirements?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Indiana does not mandate continuing education for architect or professional engineer license renewals. However, NCARB recommends 24 learning units biennially for architects, and NSPE recommends 30 PDH biennially for engineers. Indiana CPAs must complete 120 CPE hours per 3-year period, and pharmacists must complete 30 CE hours biennially."
          }
        },
        {
          "@type": "Question",
          "name": "How many CLE hours do Indiana attorneys need?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Indiana attorneys must complete 36 CLE hours per 3-year reporting period, including 3 hours of Professional Conduct. Up to 6 hours of self-study may be counted, and up to 12 excess hours may be carried forward to the next reporting period."
          }
        },
        {
          "@type": "Question",
          "name": "Which agency handles professional license renewal in Indiana?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The Indiana Professional Licensing Agency (IPLA) regulates most professions including architects, engineers, nurses, and pharmacists. The Indiana Board of Accountancy oversees CPAs, and the Indiana Supreme Court Commission for CLE administers attorney requirements."
          }
        },
        {
          "@type": "Question",
          "name": "How often must Indiana pharmacists renew their license?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Indiana pharmacists renew their license biennially and must complete 30 CE hours, including 3 hours in patient safety and 2 hours of Indiana pharmacy law, all from ACPE-accredited providers."
          }
        }
      ]
    }
  ];

  return (
    <>
      <SkipToContent />
      <SEOHelmet
        title="Indiana Professional License Renewal | CE Requirements"
        description="Complete guide to Indiana professional license renewal requirements. Track CE credits for CPAs, Attorneys, Pharmacists, Engineers, and Architects in Indiana."
        keywords="Indiana license renewal, IN CE requirements, Indiana continuing education, Indiana professional licenses, IPLA, Indiana CPA CPE, Indiana CLE, Indiana pharmacy CE"
        canonical="https://cectrack.com/in-license-renewal"
        ogImage="https://cectrack.com/og-image.png"
        structuredData={structuredData}
      />

      <section
        aria-label="Page header"
        style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', color: 'white', padding: '80px 0 60px', position: 'relative', overflow: 'hidden' }}
      >
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")", opacity: 0.4 }} />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="row justify-content-center text-center">
            <div className="col-lg-10">
              <h1 style={{ fontSize: '3rem', fontWeight: '700', marginBottom: '1.5rem', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                Indiana Professional License Renewal
              </h1>
              <p style={{ fontSize: '1.3rem', marginBottom: '2rem', opacity: 0.95 }}>
                Track CE requirements across all your Indiana professional licenses in one place.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link to="/register" className="btn btn-light btn-lg px-5 py-3" style={{ fontWeight: '600', borderRadius: '8px' }}>Start Tracking Free</Link>
                <a href="#professions" className="btn btn-outline-light btn-lg px-5 py-3" style={{ fontWeight: '600', borderRadius: '8px' }}>View Requirements</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section aria-label="Statistics" style={{ padding: '60px 0', backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row g-4 text-center">
            <div className="col-md-3"><div className="p-4"><div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.5rem' }}>6</div><div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Professions Supported</div></div></div>
            <div className="col-md-3"><div className="p-4"><div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.5rem' }}>50+</div><div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Category Requirements</div></div></div>
            <div className="col-md-3"><div className="p-4"><div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.5rem' }}>100%</div><div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Board Compliant</div></div></div>
            <div className="col-md-3"><div className="p-4"><div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.5rem' }}>24/7</div><div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Access Anywhere</div></div></div>
          </div>
        </div>
      </section>

      <main id="main-content">
        <section id="professions" aria-labelledby="professions-heading" style={{ padding: '80px 0' }}>
          <div className="container">
            <div className="text-center mb-5">
              <h2 id="professions-heading" style={{ fontSize: '2.5rem', fontWeight: '700' }}>Indiana CE Requirements by Profession</h2>
            </div>
            <div className="row g-4">
              {professions.map((prof) => (
                <div key={prof.id} className="col-md-6 col-lg-4">
                  <div
                    className="card h-100 border-0 shadow-sm"
                    style={{ transition: 'all 0.3s ease', cursor: 'pointer' }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)'; }}
                  >
                    <div className="card-body p-4">
                      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>{prof.icon}</div>
                      <h5 style={{ fontSize: '1.3rem', fontWeight: '600', marginBottom: '1rem' }}>{prof.name}</h5>
                      <div style={{ backgroundColor: '#e7f3ff', padding: '12px 16px', borderRadius: '8px', marginBottom: '1rem' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e3a8a' }}>
                          {prof.credits > 0 ? `${prof.credits} ${prof.terminology}` : 'No State Mandate'}
                        </div>
                        <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                          {prof.cycleYears === 1 ? 'Annual' : prof.cycleYears === 2 ? 'Biennial' : 'Triennial'} Renewal
                        </div>
                      </div>
                      <div style={{ marginBottom: '1rem' }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#6c757d', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Key Requirements:</div>
                        <ul style={{ fontSize: '0.9rem', margin: 0, paddingLeft: '1.2rem' }}>
                          {prof.highlights.map((h, i) => <li key={i} style={{ marginBottom: '0.3rem' }}>{h}</li>)}
                        </ul>
                      </div>
                      <div style={{ paddingTop: '1rem', borderTop: '1px solid #e9ecef', fontSize: '0.85rem', color: '#6c757d' }}>
                        <strong>{prof.stateBoard}</strong><br />
                        <a href={prof.boardUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'none' }}>Visit State Board →</a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section style={{ padding: '80px 0', backgroundColor: '#f8f9fa' }}>
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-6 mb-4 mb-lg-0">
                <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>Simplify Your Indiana License Management</h2>
                <p style={{ fontSize: '1.1rem', color: '#6c757d', marginBottom: '2rem' }}>CEC Track automatically tracks your requirements, calculates your progress, and sends timely reminders so you never miss a renewal deadline.</p>
                <ul style={{ fontSize: '1.1rem', listStyle: 'none', padding: 0 }}>
                  {[
                    'Automated deadline tracking for all Indiana professions',
                    'Upload certificates and match to requirements',
                    'Real-time progress reports by category',
                    'Multi-state support for professionals licensed in multiple states',
                    'Secure cloud storage for all documents',
                    'Email notifications for upcoming deadlines'
                  ].map((f, i) => (
                    <li key={i} style={{ marginBottom: '1rem', paddingLeft: '2rem', position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 0, color: '#10b981', fontSize: '1.5rem' }}>✓</span>{f}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="col-lg-6">
                <div className="p-5 text-center" style={{ backgroundColor: 'white', borderRadius: '16px', boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}>
                  <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📱</div>
                  <h3 style={{ fontSize: '1.8rem', fontWeight: '700', marginBottom: '1rem' }}>Start Tracking Today</h3>
                  <p style={{ color: '#6c757d', marginBottom: '2rem' }}>Join professionals across Indiana who trust CEC Track to manage their continuing education.</p>
                  <Link to="/register" className="btn btn-primary btn-lg px-5 py-3" style={{ fontWeight: '600', background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', border: 'none', borderRadius: '8px' }}>Create Free Account</Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section aria-labelledby="resources-heading" style={{ padding: '80px 0' }}>
          <div className="container">
            <div className="text-center mb-5">
              <h2 id="resources-heading" style={{ fontSize: '2.5rem', fontWeight: '700' }}>Indiana State Resources</h2>
            </div>
            <div className="row g-4">
              <div className="col-md-6">
                <article className="card border-0 shadow-sm h-100">
                  <div className="card-body p-4">
                    <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>Indiana Professional Licensing Agency</h3>
                    <p style={{ color: '#6c757d', marginBottom: '1rem' }}>Regulates architects, engineers, nurses, and pharmacists in Indiana.</p>
                    <div style={{ marginBottom: '0.5rem' }}><strong>Phone:</strong> <a href="tel:+13172340800">(317) 234-0800</a></div>
                    <div style={{ marginBottom: '0.5rem' }}><strong>Website:</strong> <a href="https://www.in.gov/pla/" target="_blank" rel="noopener noreferrer">in.gov/pla</a></div>
                    <div style={{ marginBottom: '1rem' }}><strong>Office:</strong> 402 W Washington Street, Room W072, Indianapolis, IN 46204</div>
                    <a href="https://www.in.gov/pla/" target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary">View All Professions</a>
                  </div>
                </article>
              </div>
              <div className="col-md-6">
                <article className="card border-0 shadow-sm h-100">
                  <div className="card-body p-4">
                    <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>Indiana Supreme Court — Commission for CLE</h3>
                    <p style={{ color: '#6c757d', marginBottom: '1rem' }}>Administers Continuing Legal Education requirements for Indiana attorneys.</p>
                    <div style={{ marginBottom: '0.5rem' }}><strong>Phone:</strong> <a href="tel:+13172326586">(317) 232-6586</a></div>
                    <div style={{ marginBottom: '0.5rem' }}><strong>Website:</strong> <a href="https://www.in.gov/courts/iocs/cle/" target="_blank" rel="noopener noreferrer">in.gov/courts/iocs/cle</a></div>
                    <div style={{ marginBottom: '1rem' }}><strong>Office:</strong> 30 S. Meridian Street, Suite 900, Indianapolis, IN 46204</div>
                    <a href="https://www.in.gov/courts/iocs/cle/" target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary">View CLE Requirements</a>
                  </div>
                </article>
              </div>
            </div>
          </div>
        </section>

        <OtherStates currentState="IN" />

        <section aria-label="Call to action" style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', color: 'white', padding: '80px 0' }}>
          <div className="container text-center">
            <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>Ready to Simplify Your Indiana License Renewal?</h2>
            <p style={{ fontSize: '1.25rem', marginBottom: '2rem', opacity: 0.95 }}>Join professionals across Indiana who never worry about missing CE requirements</p>
            <Link to="/register" className="btn btn-light btn-lg px-5 py-3" style={{ fontWeight: '600', borderRadius: '8px' }}>Get Started Free</Link>
          </div>
        </section>
      </main>
    </>
  );
};

export default INLicenseRenewal;
